import { CP_PORTAL_MODULES } from './cp-portal-modules.config';

/**
 * CP portal module slug → plan feature codes.
 * Keep in sync with `CP_MODULE_PRIMARY_ENTITLEMENTS` in user-ms platform constants.
 */
export const MODULE_PREFIX_FEATURES: Record<string, readonly string[]> =
  Object.fromEntries(
    CP_PORTAL_MODULES.map((module) => [module.slug, module.featureCodes]),
  );

/** Route prefix → feature codes (same source as MODULE_PREFIX_FEATURES). */
export const ROUTE_FEATURE_GROUPS = MODULE_PREFIX_FEATURES;

const MODULE_PERMISSION_ACTIONS: Record<string, readonly string[]> = {
  dashboard: ['VIEW_DASHBOARD'],
  builders: [
    'VIEW_BUILDERS',
    'CREATE_BUILDER',
    'EDIT_BUILDER',
    'DELETE_BUILDER',
  ],
  projects: [
    'VIEW_PROJECTS',
    'VIEW_MY_PROJECTS',
    'CREATE_PROJECT',
    'CREATE_MY_PROJECTS',
    'EDIT_PROJECT',
    'DELETE_PROJECT',
    'UPDATE_MY_PROJECTS',
    'DELETE_MY_PROJECTS',
    'SYNC_PROJECT',
    'PUBLISH_CP_PROJECT',
    'VIEW_CP_PUBLISH_REQUESTS',
    'VIEW_IMPORT_FROM_CP',
    'project_payment_schedule.view',
  ],
  bookings: [
    'VIEW_MY_PROJECT_BOOKINGS',
    'CREATE_MY_PROJECT_BOOKINGS',
    'MANAGE_MY_PROJECT_BOOKINGS',
    'CANCEL_MY_PROJECT_BOOKINGS',
    'VIEW_CP_LOANS',
    'MANAGE_CP_LOANS',
    'VIEW_CP_PROPERTY_REGISTRATION',
    'MANAGE_CP_PROPERTY_REGISTRATION',
    'VIEW_CP_DEMAND_PAYMENTS',
    'MANAGE_CP_DEMAND_PAYMENTS',
    'VIEW_CP_BOOKING_DOCUMENTS',
    'MANAGE_CP_BOOKING_DOCUMENTS',
    'VIEW_CP_PAYMENT_TRANSACTIONS',
  ],
  'price-management': [
    'VIEW_CP_PRICE_MANAGEMENT',
    'MANAGE_CP_PRICE_MANAGEMENT',
  ],
  campaigns: [
    'VIEW_CAMPAIGNS',
    'CREATE_CAMPAIGN',
    'EDIT_CAMPAIGN',
    'DELETE_CAMPAIGN',
  ],
  social_hub: ['VIEW_SOCIAL_HUB', 'MANAGE_SOCIAL_HUB'],
  persons: [
    'VIEW_PEOPLE',
    'CREATE_PERSON',
    'EDIT_PERSON',
    'DELETE_PERSON',
    'IMPORT_PEOPLE',
    'EXPORT_PEOPLE',
  ],
  leads: [
    'VIEW_LEADS',
    'CREATE_LEADS',
    'UPDATE_LEADS',
    'DELETE_LEADS',
    'EXPORT_LEADS',
  ],
  formiq: ['VIEW_FORMIQ', 'CREATE_FORMIQ', 'EDIT_FORMIQ', 'DELETE_FORMIQ'],
  opportunities: [
    'VIEW_OPPORTUNITIES',
    'CREATE_OPPORTUNITIES',
    'UPDATE_OPPORTUNITIES',
    'DELETE_OPPORTUNITIES',
    'EXPORT_OPPORTUNITIES',
  ],
  'calendar-events': [
    'VIEW_MEETINGS',
    'CREATE_MEETINGS',
    'UPDATE_MEETINGS',
    'DELETE_MEETINGS',
    'EXPORT_MEETINGS',
  ],
  'my-quotations': [
    'VIEW_OPPORTUNITIES',
    'CREATE_OPPORTUNITIES',
    'UPDATE_OPPORTUNITIES',
    'DELETE_OPPORTUNITIES',
    'EXPORT_OPPORTUNITIES',
  ],
  activities: [
    'VIEW_LEADS',
    'VIEW_OPPORTUNITIES',
    'CREATE_LEADS',
    'UPDATE_LEADS',
  ],
  'site-visits': [
    'VIEW_SITE_VISITS',
    'CREATE_SITE_VISIT',
    'EDIT_SITE_VISIT',
    'DELETE_SITE_VISIT',
  ],
  offers: ['VIEW_OFFERS', 'CREATE_OFFER', 'EDIT_OFFER', 'DELETE_OFFER'],
  'incentive-reports': ['VIEW_INCENTIVE_REPORTS', 'EXPORT_INCENTIVE_REPORTS'],
  invoices: [
    'VIEW_INVOICES',
    'CREATE_INVOICES',
    'UPDATE_INVOICES',
    'DELETE_INVOICES',
  ],
  payments: ['VIEW_PAYMENTS', 'CREATE_PAYMENT', 'EDIT_PAYMENT', 'DELETE_PAYMENT'],
  integrations: [
    'VIEW_VENDOR_CONFIGURATION',
    'VIEW_VENDOR_API',
    'VIEW_VENDOR_API_LOGS',
    'VIEW_VENDOR_LOGS',
    'CREATE_VENDOR_CONFIGURATION',
    'CREATE_VENDOR_API',
    'UPDATE_VENDOR_CONFIGURATION',
    'UPDATE_VENDOR_API',
    'DELETE_VENDOR_CONFIGURATION',
    'DELETE_VENDOR_API',
    'MANAGE_INTEGRATIONS',
    'VIEW_INTEGRATIONS',
    'VIEW_SOCIAL_HUB',
    'MANAGE_SOCIAL_HUB',
    'VIEW_CP_DOCUMENT_TEMPLATES',
    'VIEW_CP_EMAIL_TEMPLATES',
    'VIEW_CP_WHATSAPP_TEMPLATES',
    'MANAGE_CP_DOCUMENT_TEMPLATES',
    'MANAGE_CP_EMAIL_TEMPLATES',
    'MANAGE_CP_WHATSAPP_TEMPLATES',
    'MANAGE_VENDOR_APIS',
    'CREATE_VENDOR_API_LOGS',
    'UPDATE_VENDOR_API_LOGS',
    'DELETE_VENDOR_API_LOGS',
  ],
  bank: [
    'bank_apf.view',
    'bank_apf.manage',
    'banks_master.view',
    'bank_offers.view',
    'bank_offers.manage',
    'bank_fees.view',
    'bank_fees.manage',
  ],
  user: [
    'VIEW_INTERNAL_USERS',
    'CREATE_INTERNAL_USERS',
    'UPDATE_INTERNAL_USERS',
    'DELETE_INTERNAL_USERS',
    'MANAGE_USERS',
    'MANAGE_ROLES',
    'VIEW_ROLES_PERMISSIONS',
    'CREATE_ROLES_PERMISSIONS',
    'UPDATE_ROLES_PERMISSIONS',
    'DELETE_ROLES_PERMISSIONS',
    'VIEW_DEPARTMENTS',
    'CREATE_DEPARTMENTS',
    'UPDATE_DEPARTMENTS',
    'DELETE_DEPARTMENTS',
    'MANAGE_DEPARTMENTS',
  ],
  'rules-and-automations': [
    'VIEW_ASSIGNMENT_RULES',
    'CREATE_ASSIGNMENT_RULES',
    'UPDATE_ASSIGNMENT_RULES',
    'DELETE_ASSIGNMENT_RULES',
    'VIEW_APPROVAL_RULES',
    'CREATE_APPROVAL_RULES',
    'UPDATE_APPROVAL_RULES',
    'DELETE_APPROVAL_RULES',
    'VIEW_CP_GENERIC_MASTERS',
    'MANAGE_CP_GENERIC_MASTERS',
  ],
  'scoring-rules': [
    'VIEW_SCORING_RULES',
    'CREATE_SCORING_RULES',
    'UPDATE_SCORING_RULES',
    'DELETE_SCORING_RULES',
  ],
  'approval-management': ['VIEW_APPROVAL_MANAGEMENT'],
  companies: ['VIEW_COMPANIES', 'MANAGE_COMPANIES'],
  settings: [
    'VIEW_SETTINGS',
    'EDIT_SETTINGS',
    'VIEW_IP_WHITELIST',
    'CREATE_IP_WHITELIST',
    'UPDATE_IP_WHITELIST',
    'DELETE_IP_WHITELIST',
  ],
  system: ['VIEW_RECYCLE_BIN', 'RESTORE_RECYCLE_BIN', 'DELETE_RECYCLE_BIN'],
  'audit-logs': ['VIEW_AUDIT_LOGS'],
};

/** Maps RBAC permission actions to module slug prefixes for entitlement checks. */
export const PERMISSION_MODULE_SLUG: Record<string, string> = Object.fromEntries(
  Object.entries(MODULE_PERMISSION_ACTIONS).flatMap(([slug, actions]) =>
    actions.map((action) => [action, slug]),
  ),
);

/** True when the active plan includes the module tied to a permission action. */
export function isPermissionCoveredByEntitlements(
  permission: string,
  canAccessModule: (slug: string) => boolean,
  entitlementsResolved: boolean,
): boolean {
  if (!entitlementsResolved) {
    return false;
  }
  const slug = PERMISSION_MODULE_SLUG[permission];
  if (!slug) {
    return false;
  }
  return canAccessModule(slug);
}

/** True when any listed permission maps to a plan-included module. */
export function areAnyPermissionsCoveredByEntitlements(
  permissions: string[],
  canAccessModule: (slug: string) => boolean,
  entitlementsResolved: boolean,
): boolean {
  return permissions.some((permission) =>
    isPermissionCoveredByEntitlements(
      permission,
      canAccessModule,
      entitlementsResolved,
    ),
  );
}
